import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  FormControlLabel,
  Switch,
  Grid,
  Paper,
  InputAdornment
} from '@mui/material';

import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  School as SchoolIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Search,
  FilterList
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

interface Course {
  id: string;
  title: string;
  description: string;
  category: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  estimatedDuration: number;
  lessons: any[];
  evaluations: any[];
  isActive: boolean;
}

interface Lesson {
  id?: string;
  _id?: string;
  title: string;
  content: string;
  contentType: 'text' | 'video' | 'interactive' | 'document';
  mediaUrl?: string;
  duration: number;
  order: number;
  objectives: string[];
  evaluationId?: string; // ID de la evaluación asociada a esta lección
  resources: {
    title: string;
    url: string;
    type: 'document' | 'link' | 'video' | 'tool';
  }[];
}

interface CourseFormData {
  title: string;
  description: string;
  category: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  estimatedDuration: number;
  targetVulnerabilities: string[];
  tags: string[];
  lessons: Lesson[];
  evaluations: string[];
  passThreshold: number;
}

const AdminCourses: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [filteredCourses, setFilteredCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [openLessonDialog, setOpenLessonDialog] = useState(false);
  const [editingLesson, setEditingLesson] = useState<Lesson | null>(null);
  const [lessonFormData, setLessonFormData] = useState<Lesson>({
    title: '',
    content: '',
    contentType: 'text',
    mediaUrl: '',
    duration: 0,
    order: 0,
    objectives: [],
    resources: []
  });
  const [formData, setFormData] = useState<CourseFormData>({
    title: '',
    description: '',
    category: '',
    difficulty: 'beginner',
    estimatedDuration: 0,
    targetVulnerabilities: [],
    tags: [],
    lessons: [],
    evaluations: [],
    passThreshold: 70
  });
  const [availableEvaluations, setAvailableEvaluations] = useState<any[]>([]);

  // Estados para filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  // Dificultades disponibles
  const difficulties = [
    { value: 'beginner', label: 'Principiante' },
    { value: 'intermediate', label: 'Intermedio' },
    { value: 'advanced', label: 'Avanzado' }
  ];

  // Estados disponibles
  const statuses = [
    { value: 'active', label: 'Activo' },
    { value: 'inactive', label: 'Inactivo' }
  ];

  const categories = [
    'phishing_prevention',
    'ransomware_protection',
    'social_engineering',
    'device_security',
    'password_management',
    'data_protection',
    'email_security',
    'web_browsing',
    'mobile_security',
    'incident_response'
  ];

  // Calcular contadores para chips
  const categoryCounts = categories.reduce((acc, cat) => {
    acc[cat] = courses.filter(c => c.category === cat).length;
    return acc;
  }, {} as Record<string, number>);
  const difficultyCounts = difficulties.reduce((acc, diff) => {
    acc[diff.value] = courses.filter(c => c.difficulty === diff.value).length;
    return acc;
  }, {} as Record<string, number>);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchCourses();
    fetchEvaluations();
  }, [user, navigate]);

  // Aplicar filtros cuando cambien los valores
  useEffect(() => {
    applyFilters();
  }, [courses, searchTerm, selectedCategory, selectedDifficulty, selectedStatus]);

  const fetchEvaluations = async () => {
    try {
      const response = await api.getEvaluations();
      setAvailableEvaluations(response.evaluations);
    } catch (err: any) {
      console.error('Error cargando evaluaciones:', err);
    }
  };

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const response = await api.getAllCourses({ includeContent: true });
      const transformedCourses = response.courses.map((course: any) => ({
        id: course._id || course.id,
        title: course.title,
        description: course.description,
        category: course.category,
        difficulty: course.difficulty,
        estimatedDuration: course.estimatedDuration,
        lessons: course.lessons || [],
        evaluations: course.evaluations || [],
        isActive: course.settings?.isActive ?? true,
        passThreshold: course.settings?.passThreshold ?? 70,
      }));
      setCourses(transformedCourses);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar los cursos');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...courses];

    // Filtro por búsqueda
    if (searchTerm) {
      filtered = filtered.filter(course =>
        course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtro por categoría
    if (selectedCategory) {
      filtered = filtered.filter(course => course.category === selectedCategory);
    }

    // Filtro por dificultad
    if (selectedDifficulty) {
      filtered = filtered.filter(course => course.difficulty === selectedDifficulty);
    }

    // Filtro por estado
    if (selectedStatus) {
      filtered = filtered.filter(course => {
        if (selectedStatus === 'active') return course.isActive;
        if (selectedStatus === 'inactive') return !course.isActive;
        return true;
      });
    }

    setFilteredCourses(filtered);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedDifficulty('');
    setSelectedStatus('');
  };

  const getCategoryLabel = (category: string) => {
    const categoryLabels: { [key: string]: string } = {
      'phishing_prevention': 'Prevención de Phishing',
      'ransomware_protection': 'Protección contra Ransomware',
      'social_engineering': 'Ingeniería Social',
      'device_security': 'Seguridad de Dispositivos',
      'password_management': 'Gestión de Contraseñas',
      'data_protection': 'Protección de Datos',
      'email_security': 'Seguridad de Email',
      'web_browsing': 'Navegación Web Segura',
      'mobile_security': 'Seguridad Móvil',
      'incident_response': 'Respuesta a Incidentes'
    };
    return categoryLabels[category] || category;
  };

  const handleOpenDialog = (course?: Course) => {
    if (course) {
      setEditingCourse(course);
      setFormData({
        title: course.title,
        description: course.description,
        category: course.category,
        difficulty: course.difficulty,
        estimatedDuration: course.estimatedDuration,
        targetVulnerabilities: [],
        tags: [],
        lessons: course.lessons || [],
        evaluations: course.evaluations || [],
        passThreshold: (course as any).passThreshold ?? 70
      });
    } else {
      setEditingCourse(null);
      setFormData({
        title: '',
        description: '',
        category: '',
        difficulty: 'beginner',
        estimatedDuration: 0,
        targetVulnerabilities: [],
        tags: [],
        lessons: [],
        evaluations: [],
        passThreshold: 70
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCourse(null);
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
        settings: {
          passThreshold: formData.passThreshold ?? 70
        }
      };
      if (editingCourse) {
        await api.updateCourse(editingCourse.id, payload);
      } else {
        await api.createCourse(payload);
      }
      handleCloseDialog();
      fetchCourses();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al guardar el curso');
    }
  };

  const handleDeleteCourse = async (courseId: string) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este curso?')) {
      try {
        await api.deleteCourse(courseId);
        fetchCourses();
      } catch (err: any) {
        setError(err.response?.data?.message || 'Error al eliminar el curso');
      }
    }
  };

  // Funciones para gestionar lecciones
  const handleOpenLessonDialog = (lesson?: Lesson) => {
    if (lesson) {
      setEditingLesson(lesson);
      setLessonFormData({
        id: lesson._id || lesson.id, // Preferir _id sobre id
        title: lesson.title || '',
        content: lesson.content || '',
        contentType: lesson.contentType || 'text',
        mediaUrl: lesson.mediaUrl || '',
        duration: lesson.duration || 0,
        order: lesson.order || 0,
        objectives: lesson.objectives || [],
        evaluationId: lesson.evaluationId || undefined, // Incluir evaluationId
        resources: lesson.resources || []
      });
    } else {
      setEditingLesson(null);
      setLessonFormData({
        title: '',
        content: '',
        contentType: 'text',
        mediaUrl: '',
        duration: 0,
        order: formData.lessons.length + 1,
        objectives: [],
        evaluationId: undefined, // Incluir evaluationId
        resources: []
      });
    }
    setOpenLessonDialog(true);
  };

  const handleCloseLessonDialog = () => {
    setOpenLessonDialog(false);
    setEditingLesson(null);
  };

  const handleRemoveLesson = (lessonId: string) => {
    const updatedLessons = formData.lessons.filter(lesson => {
      const currentId = lesson._id || lesson.id;
      return currentId !== lessonId;
    });
    
    const newFormData = {
      ...formData,
      lessons: updatedLessons,
      estimatedDuration: updatedLessons.reduce((total, lesson) => total + (lesson.duration || 0), 0)
    };
    
    setFormData(newFormData);
  };

  const handleSubmitLesson = async () => {
    if (!lessonFormData.title || !lessonFormData.title.trim()) {
      setError('El título de la lección es obligatorio');
      return;
    }
    
    if (!lessonFormData.duration || lessonFormData.duration <= 0) {
      setError('La duración debe ser mayor a 0');
      return;
    }

    // Validaciones específicas según el tipo de contenido
    if (lessonFormData.contentType === 'text' && (!lessonFormData.content || !lessonFormData.content.trim())) {
      setError('El contenido de texto es obligatorio para lecciones de tipo texto');
      return;
    }

    if (lessonFormData.contentType === 'video' && (!lessonFormData.mediaUrl || !lessonFormData.mediaUrl.trim())) {
      setError('La URL del video es obligatoria para lecciones de tipo video');
      return;
    }

    if (lessonFormData.contentType === 'document' && (!lessonFormData.mediaUrl || !lessonFormData.mediaUrl.trim())) {
      setError('La URL del documento es obligatoria para lecciones de tipo documento');
      return;
    }

    const updatedLessons = [...formData.lessons];
    
    if (editingLesson) {
      // Actualizar lección existente usando el identificador único
      const index = updatedLessons.findIndex(l => {
        const lessonIdentifier = l._id || l.id;
        const editingIdentifier = editingLesson._id || editingLesson.id;
        return lessonIdentifier === editingIdentifier;
      });
      
      if (index !== -1) {
        updatedLessons[index] = { ...lessonFormData };
      }
    } else {
      // Agregar nueva lección
      const newLesson = {
        ...lessonFormData
        // No incluir id para nuevas lecciones, Mongoose generará el _id automáticamente
      };
      updatedLessons.push(newLesson);
    }

    // Reordenar lecciones
    const sortedLessons = updatedLessons.sort((a, b) => a.order - b.order);

    const newFormData = {
      ...formData,
      lessons: sortedLessons,
      estimatedDuration: sortedLessons.reduce((total, lesson) => total + (lesson.duration || 0), 0)
    };

    setFormData(newFormData);
    
    // Guardar cambios en el backend si estamos editando un curso
    if (editingCourse) {
      try {
        await api.updateCourse(editingCourse.id, newFormData);
      } catch (err: any) {
        console.error('Error actualizando curso:', err);
        setError(err.response?.data?.message || 'Error al actualizar el curso');
        return;
      }
    }
    
    handleCloseLessonDialog();
  };

  const handleDeleteLesson = async (lessonId: string) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta lección?')) {
      const updatedLessons = formData.lessons.filter(l => {
        const lessonIdentifier = l._id || l.id;
        return lessonIdentifier !== lessonId;
      });
      
      // Reordenar lecciones restantes
      updatedLessons.forEach((lesson, index) => {
        lesson.order = index + 1;
      });

      const newFormData = {
        ...formData,
        lessons: updatedLessons,
        estimatedDuration: updatedLessons.reduce((total, lesson) => total + lesson.duration, 0)
      };

      setFormData(newFormData);
      
      // Guardar cambios en el backend si estamos editando un curso
      if (editingCourse) {
        try {
                  await api.updateCourse(editingCourse.id, newFormData);
        } catch (err: any) {
          console.error('Error actualizando curso:', err);
          setError(err.response?.data?.message || 'Error al actualizar el curso');
          return;
        }
      }
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'success';
      case 'intermediate': return 'warning';
      case 'advanced': return 'error';
      default: return 'default';
    }
  };

  const getDifficultyLabel = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'Básico';
      case 'intermediate': return 'Intermedio';
      case 'advanced': return 'Avanzado';
      default: return difficulty;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" display="flex" alignItems="center" gap={1}>
          <SchoolIcon />
          Administración de Cursos
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nuevo Curso
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Layout principal con filtros a la izquierda */}
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
        {/* Filtros - Panel lateral */}
        <Paper sx={{ p: 3, minWidth: 280, maxWidth: 320, position: 'sticky', top: 20 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <FilterList sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" color="primary">
              Filtros
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Búsqueda */}
            <TextField
              fullWidth
              label="Buscar cursos"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por título o descripción..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />

            {/* Chips de Categoría */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Categoría
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  label="Todos"
                  clickable
                  color={selectedCategory === '' ? 'primary' : 'default'}
                  onClick={() => setSelectedCategory('')}
                  sx={{ fontWeight: selectedCategory === '' ? 'bold' : 'normal' }}
                />
                {categories.map((cat) => (
                  <Chip
                    key={cat}
                    label={
                      <span>
                        {getCategoryLabel(cat)}{' '}
                        <span style={{ fontWeight: 'bold', fontSize: 13, opacity: 0.7 }}>
                          {categoryCounts[cat] > 0 ? `(${categoryCounts[cat]})` : ''}
                        </span>
                      </span>
                    }
                    clickable
                    color={selectedCategory === cat ? 'primary' : 'default'}
                    onClick={() => setSelectedCategory(cat)}
                    sx={{ fontWeight: selectedCategory === cat ? 'bold' : 'normal' }}
                    disabled={categoryCounts[cat] === 0}
                  />
                ))}
              </Box>
            </Box>

            {/* Chips de Dificultad */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Dificultad
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  label="Todas"
                  clickable
                  color={selectedDifficulty === '' ? 'primary' : 'default'}
                  onClick={() => setSelectedDifficulty('')}
                  sx={{ fontWeight: selectedDifficulty === '' ? 'bold' : 'normal' }}
                />
                {difficulties.map((diff) => (
                  <Chip
                    key={diff.value}
                    label={
                      <span>
                        {diff.label}{' '}
                        <span style={{ fontWeight: 'bold', fontSize: 13, opacity: 0.7 }}>
                          {difficultyCounts[diff.value] > 0 ? `(${difficultyCounts[diff.value]})` : ''}
                        </span>
                      </span>
                    }
                    clickable
                    color={selectedDifficulty === diff.value ? 'primary' : 'default'}
                    onClick={() => setSelectedDifficulty(diff.value)}
                    sx={{ fontWeight: selectedDifficulty === diff.value ? 'bold' : 'normal' }}
                    disabled={difficultyCounts[diff.value] === 0}
                  />
                ))}
              </Box>
            </Box>

            {/* Filtro por estado */}
            <FormControl fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                value={selectedStatus}
                label="Estado"
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <MenuItem value="">Todos los estados</MenuItem>
                {statuses.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Botón limpiar filtros */}
            <Button
              variant={(searchTerm || selectedCategory || selectedDifficulty || selectedStatus) ? "contained" : "outlined"}
              onClick={clearFilters}
              size="small"
              color={(searchTerm || selectedCategory || selectedDifficulty || selectedStatus) ? "error" : "primary"}
              sx={{ 
                mt: 1,
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: (searchTerm || selectedCategory || selectedDifficulty || selectedStatus) ? 'scale(1.05)' : 'none',
                }
              }}
            >
              Limpiar Filtros
            </Button>

            {/* Información de resultados */}
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {filteredCourses.length} de {courses.length} cursos
              </Typography>
              {(searchTerm || selectedCategory || selectedDifficulty || selectedStatus) && (
                <Chip
                  label={`Filtros activos: ${[
                    searchTerm && 'Búsqueda',
                    selectedCategory && 'Categoría',
                    selectedDifficulty && 'Dificultad',
                    selectedStatus && 'Estado'
                  ].filter(Boolean).join(', ')}`}
                  color="primary"
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
          </Box>
        </Paper>

        {/* Lista de cursos - Contenido principal */}
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 3 }}>
        {filteredCourses.map((course) => (
          <Card key={course.id}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Typography variant="h6" component="h2" sx={{ flexGrow: 1, mr: 1 }}>
                  {course.title}
                </Typography>
                <Box>
                  <Tooltip title="Ver curso">
                    <IconButton 
                      size="small" 
                      onClick={() => navigate(`/courses/${course.id}`)}
                      color="primary"
                    >
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Editar curso">
                    <IconButton 
                      size="small" 
                      onClick={() => handleOpenDialog(course)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Eliminar curso">
                    <IconButton 
                      size="small" 
                      onClick={() => handleDeleteCourse(course.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {course.description}
              </Typography>

              <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                <Chip 
                  label={getCategoryLabel(course.category)} 
                  size="small" 
                  variant="outlined"
                />
                <Chip 
                  label={getDifficultyLabel(course.difficulty)}
                  size="small"
                  color={getDifficultyColor(course.difficulty) as any}
                />
                <Chip 
                  label={`${course.estimatedDuration} min`}
                  size="small"
                  variant="outlined"
                />
              </Box>

              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  {course.lessons.length} lecciones
                </Typography>
                <Chip 
                  label={course.isActive ? 'Activo' : 'Inactivo'}
                  size="small"
                  color={course.isActive ? 'success' : 'default'}
                />
              </Box>
            </CardContent>
          </Card>
        ))}
          </Box>

          {/* Mensaje cuando no hay cursos */}
          {filteredCourses.length === 0 && !loading && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <SchoolIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {courses.length === 0 ? 'No se encontraron cursos' : 'No hay cursos que coincidan con los filtros'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {courses.length === 0 
                  ? 'Crea tu primer curso haciendo clic en "Nuevo Curso"'
                  : 'Intenta ajustar los filtros de búsqueda'
                }
              </Typography>
              {courses.length > 0 && (
                <Button variant="outlined" onClick={clearFilters}>
                  Limpiar filtros
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Dialog para crear/editar curso */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCourse ? 'Editar Curso' : 'Crear Nuevo Curso'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Título del curso"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Descripción"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
              required
            />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: 200, flex: 1 }} required>
                <InputLabel>Categoría</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  label="Categoría"
                >
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category.replace('_', ' ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 200, flex: 1 }} required>
                <InputLabel>Dificultad</InputLabel>
                <Select
                  value={formData.difficulty}
                  onChange={(e) => setFormData({ ...formData, difficulty: e.target.value as any })}
                  label="Dificultad"
                >
                  <MenuItem value="beginner">Básico</MenuItem>
                  <MenuItem value="intermediate">Intermedio</MenuItem>
                  <MenuItem value="advanced">Avanzado</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <TextField
              fullWidth
              label="Duración estimada (minutos)"
              type="number"
              value={formData.estimatedDuration}
              onChange={(e) => setFormData({ ...formData, estimatedDuration: parseInt(e.target.value) })}
              required
            />
            <TextField
              fullWidth
              label="Porcentaje mínimo de aprobación (%)"
              type="number"
              value={formData.passThreshold}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  passThreshold: Math.min(100, Math.max(0, parseInt(e.target.value) || 0))
                })
              }
              inputProps={{ min: 0, max: 100 }}
              helperText="Valor base del curso. Cada empresa puede sobrescribirlo en Gestión de Empresas."
            />

            {/* Sección de Lecciones */}
            <Box sx={{ mt: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Lecciones ({formData.lessons.length})</Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenLessonDialog()}
                  size="small"
                >
                  Agregar Lección
                </Button>
              </Box>
              
              {formData.lessons.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  <Typography variant="body2">
                    No hay lecciones agregadas. Haz clic en "Agregar Lección" para comenzar.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {formData.lessons.map((lesson, index) => (
                    <Card key={index} variant="outlined">
                      <CardContent sx={{ py: 2, px: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                          <Box sx={{ flexGrow: 1 }}>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {lesson.title}
                              </Typography>
                              <Chip 
                                label={lesson.contentType} 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                              />
                              {lesson.evaluationId && (
                                <Chip 
                                  label="Con Evaluación" 
                                  size="small" 
                                  color="success" 
                                  variant="outlined"
                                />
                              )}
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              Duración: {lesson.duration} min • Orden: {lesson.order}
                            </Typography>
                            {lesson.objectives.length > 0 && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Objetivos: {lesson.objectives.length}
                              </Typography>
                            )}
                          </Box>
                          <Box display="flex" gap={1}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenLessonDialog(lesson)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteLesson(lesson._id || lesson.id || '')}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={!formData.title || !formData.description || !formData.category}
          >
            {editingCourse ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para crear/editar lección */}
      <Dialog open={openLessonDialog} onClose={handleCloseLessonDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingLesson ? 'Editar Lección' : 'Crear Nueva Lección'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Título de la lección"
              value={lessonFormData.title}
              onChange={(e) => setLessonFormData({ ...lessonFormData, title: e.target.value })}
              required
            />
            
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: 200, flex: 1 }} required>
                <InputLabel>Tipo de contenido</InputLabel>
                <Select
                  value={lessonFormData.contentType}
                  onChange={(e) => setLessonFormData({ ...lessonFormData, contentType: e.target.value as any })}
                  label="Tipo de contenido"
                >
                  <MenuItem value="text">Texto</MenuItem>
                  <MenuItem value="video">Video</MenuItem>
                  <MenuItem value="interactive">Interactivo</MenuItem>
                  <MenuItem value="document">Documento</MenuItem>
                </Select>
              </FormControl>
              
              <TextField
                sx={{ minWidth: 150, flex: 1 }}
                label="Duración (minutos)"
                type="number"
                value={lessonFormData.duration}
                onChange={(e) => setLessonFormData({ ...lessonFormData, duration: parseInt(e.target.value) })}
                required
              />

              <TextField
                sx={{ minWidth: 100, flex: 1 }}
                label="Orden"
                type="number"
                value={lessonFormData.order}
                onChange={(e) => setLessonFormData({ ...lessonFormData, order: parseInt(e.target.value) })}
                required
              />
            </Box>

            {/* Selección de evaluación asociada */}
            <FormControl fullWidth>
              <InputLabel>Evaluación asociada (opcional)</InputLabel>
              <Select
                value={lessonFormData.evaluationId || ''}
                onChange={(e) => setLessonFormData({ ...lessonFormData, evaluationId: e.target.value || undefined })}
                label="Evaluación asociada (opcional)"
              >
                <MenuItem value="">
                  <em>Sin evaluación</em>
                </MenuItem>
                {availableEvaluations.map((evaluation) => (
                  <MenuItem key={evaluation._id} value={evaluation._id}>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {evaluation.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {evaluation.category} • {evaluation.questions.length} preguntas • {evaluation.settings.timeLimit} min
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Si seleccionas una evaluación, los estudiantes deberán aprobarla para completar esta lección
              </Typography>
            </FormControl>

            {/* Campos específicos según el tipo de contenido */}
            {lessonFormData.contentType === 'text' && (
              <TextField
                fullWidth
                label="Contenido de texto"
                value={lessonFormData.content || ''}
                onChange={(e) => setLessonFormData({ ...lessonFormData, content: e.target.value })}
                multiline
                rows={6}
                placeholder="Escribe el contenido de la lección aquí..."
                helperText="Este contenido se mostrará como texto en la lección"
              />
            )}

            {lessonFormData.contentType === 'video' && (
              <TextField
                fullWidth
                label="URL del video"
                value={lessonFormData.mediaUrl || ''}
                onChange={(e) => setLessonFormData({ ...lessonFormData, mediaUrl: e.target.value })}
                placeholder="https://ejemplo.com/video.mp4"
                helperText="Ingresa la URL del video (YouTube, Vimeo, o archivo directo)"
              />
            )}

            {lessonFormData.contentType === 'document' && (
              <TextField
                fullWidth
                label="URL del documento"
                value={lessonFormData.mediaUrl || ''}
                onChange={(e) => setLessonFormData({ ...lessonFormData, mediaUrl: e.target.value })}
                placeholder="https://ejemplo.com/documento.pdf"
                helperText="Ingresa la URL del documento (PDF, PowerPoint, etc.)"
              />
            )}

            {lessonFormData.contentType === 'interactive' && (
              <Box>
                <TextField
                  fullWidth
                  label="Descripción de la actividad interactiva"
                  value={lessonFormData.content || ''}
                  onChange={(e) => setLessonFormData({ ...lessonFormData, content: e.target.value })}
                  multiline
                  rows={4}
                  placeholder="Describe la actividad interactiva que se realizará..."
                  helperText="Describe qué tipo de actividad interactiva se realizará en esta lección"
                />
                <TextField
                  fullWidth
                  label="URL de la herramienta interactiva (opcional)"
                  value={lessonFormData.mediaUrl || ''}
                  onChange={(e) => setLessonFormData({ ...lessonFormData, mediaUrl: e.target.value })}
                  placeholder="https://ejemplo.com/herramienta-interactiva"
                  helperText="Si la actividad usa una herramienta externa, ingresa su URL"
                  sx={{ mt: 2 }}
                />
              </Box>
            )}

            <Box>
              <Typography variant="subtitle2" mb={1}>Objetivos de aprendizaje</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {lessonFormData.objectives.map((objective, index) => (
                  <Box key={index} display="flex" gap={1}>
                    <TextField
                      fullWidth
                      size="small"
                      value={objective}
                      onChange={(e) => {
                        const newObjectives = [...lessonFormData.objectives];
                        newObjectives[index] = e.target.value;
                        setLessonFormData({ ...lessonFormData, objectives: newObjectives });
                      }}
                      placeholder="Objetivo de aprendizaje"
                    />
                    <IconButton
                      size="small"
                      onClick={() => {
                        const newObjectives = lessonFormData.objectives.filter((_, i) => i !== index);
                        setLessonFormData({ ...lessonFormData, objectives: newObjectives });
                      }}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setLessonFormData({
                    ...lessonFormData,
                    objectives: [...lessonFormData.objectives, '']
                  })}
                >
                  Agregar Objetivo
                </Button>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLessonDialog}>Cancelar</Button>
          <Button 
            onClick={handleSubmitLesson} 
            variant="contained"
            disabled={
              !lessonFormData.title || 
              !lessonFormData.duration || 
              lessonFormData.duration <= 0 ||
              (lessonFormData.contentType === 'text' && !lessonFormData.content?.trim()) ||
              (lessonFormData.contentType === 'video' && !lessonFormData.mediaUrl?.trim()) ||
              (lessonFormData.contentType === 'document' && !lessonFormData.mediaUrl?.trim())
            }
          >
            {editingLesson ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminCourses; 